import { ImageSquareIcon } from '@phosphor-icons/react';
import { useEffect, useState, type JSX } from 'react';

import { AttachmentThumb } from '@/components/issue-reporting/admin/AttachmentThumb';
import { IssueDetailDialog } from '@/components/issue-reporting/admin/IssueDetailDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIssueReportsQuery } from '@/hooks/useIssueReportQueries';
import {
  CATEGORY_LABEL,
  SEVERITY_LABEL,
  STATUS_LABEL,
  formatDateTime,
  severityBadgeVariant,
  statusBadgeVariant,
} from '@/lib/issue-reporting/display';
import {
  IssueCategory,
  IssueSeverity,
  IssueStatus,
  type IssueReportListQuery,
} from '@/types/issue-reports';

const PAGE_SIZE = 20;
const ANY = '__any__';

export function IssuesTable(): JSX.Element {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(ANY);
  const [severityFilter, setSeverityFilter] = useState<string>(ANY);
  const [categoryFilter, setCategoryFilter] = useState<string>(ANY);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => { setDebouncedSearch(search); }, 300);
    return () => { window.clearTimeout(t); };
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, severityFilter, categoryFilter]);

  const query: IssueReportListQuery = {
    ...(debouncedSearch !== '' ? { q: debouncedSearch } : {}),
    ...(statusFilter !== ANY ? { status: statusFilter as IssueStatus } : {}),
    ...(severityFilter !== ANY ? { severity: severityFilter as IssueSeverity } : {}),
    ...(categoryFilter !== ANY ? { category: categoryFilter as IssueCategory } : {}),
    page,
    pageSize: PAGE_SIZE,
  };

  const issuesQuery = useIssueReportsQuery(query);
  const total = issuesQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = issuesQuery.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px] flex-1 space-y-1">
          <Label htmlFor="issues-search">Search</Label>
          <Input
            id="issues-search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); }}
            placeholder="Description, reporter name or email…"
            data-testid="issues-search-input"
          />
        </div>
        <div className="w-40 space-y-1">
          <Label htmlFor="issues-status">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="issues-status" data-testid="issues-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All statuses</SelectItem>
              {Object.values(IssueStatus).map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40 space-y-1">
          <Label htmlFor="issues-severity">Severity</Label>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger id="issues-severity" data-testid="issues-severity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All severities</SelectItem>
              {Object.values(IssueSeverity).map((s) => (
                <SelectItem key={s} value={s}>
                  {SEVERITY_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44 space-y-1">
          <Label htmlFor="issues-category">Category</Label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger id="issues-category" data-testid="issues-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All categories</SelectItem>
              {Object.values(IssueCategory).map((c) => (
                <SelectItem key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {issuesQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="issues-loading">
          Loading issues…
        </p>
      )}
      {issuesQuery.isError && (
        <p className="text-sm text-destructive" data-testid="issues-error">
          Failed to load issues.
        </p>
      )}

      {issuesQuery.data !== undefined && (
        <>
          <div className="overflow-x-auto">
            <Table data-testid="issues-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Shot</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Reported</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center">
                      <span data-testid="issues-empty">No issues found.</span>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer"
                      onClick={() => { setSelectedId(r.id); }}
                      data-testid={`issue-row-${String(r.id)}`}
                    >
                      <TableCell>
                        {r.firstAttachmentId !== null ? (
                          <AttachmentThumb
                            reportId={r.id}
                            attachmentId={r.firstAttachmentId}
                            alt="screenshot"
                          />
                        ) : (
                          <span className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-input text-muted-foreground">
                            <ImageSquareIcon size={16} />
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <span className="line-clamp-2 text-sm">{r.description}</span>
                        {r.attachmentCount > 1 && (
                          <span className="text-xs text-muted-foreground">
                            +{String(r.attachmentCount - 1)} more image(s)
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={severityBadgeVariant(r.severity)}>
                          {SEVERITY_LABEL[r.severity]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{CATEGORY_LABEL[r.category]}</TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(r.status)}>{STATUS_LABEL[r.status]}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.reportedByName ?? `User #${String(r.reportedByUserId)}`}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDateTime(r.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-muted-foreground" data-testid="issues-pagination-summary">
              Page {page} of {pageCount} • {total} total
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
                disabled={page <= 1}
                data-testid="issues-prev"
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setPage((p) => Math.min(pageCount, p + 1)); }}
                disabled={page >= pageCount}
                data-testid="issues-next"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {selectedId !== null && (
        <IssueDetailDialog
          reportId={selectedId}
          open={true}
          onOpenChange={(o) => {
            if (!o) setSelectedId(null);
          }}
        />
      )}
    </div>
  );
}
